/**
 * RuntimeRecovery — Error detection and logging ONLY.
 * 
 * ARCHITECTURAL RULE: This module NEVER navigates, reloads, or calls signOut.
 * It only detects and logs runtime errors for diagnostics.
 */

// Declared via Vite `define` — replaced at build time as a bare identifier
declare const __APP_BUILD_ID__: string;

type RecoveryReason = "asset-load-error" | "chunk-load-error" | "unhandled-rejection" | "map-runtime-error";

function getBuildFingerprint(): string {
  try {
    return typeof __APP_BUILD_ID__ !== "undefined" ? __APP_BUILD_ID__ : "unknown";
  } catch {
    return "unknown";
  }
}

function extractMessage(reason: unknown): string {
  if (typeof reason === "string") return reason;
  if (reason instanceof Error) return reason.message;
  if (typeof reason === "object" && reason && "message" in reason) {
    return String((reason as { message: unknown }).message ?? "");
  }
  return "";
}

function isChunkLoadFailure(message: string): boolean {
  const normalized = message.toLowerCase();
  return (
    normalized.includes("chunkloaderror") ||
    normalized.includes("failed to fetch dynamically imported module") ||
    normalized.includes("importing a module script failed") ||
    normalized.includes("loading css chunk")
  );
}

/**
 * Log runtime error — NO recovery action taken.
 * Navigation and reload are strictly forbidden.
 */
function logRuntimeError(reason: RecoveryReason, details?: Record<string, unknown>) {
  console.error("[RuntimeRecovery] Falha detectada (registro apenas)", {
    reason,
    details,
    buildId: getBuildFingerprint(),
    timestamp: new Date().toISOString(),
  });
}

export function triggerRuntimeRecovery(reason: RecoveryReason, details?: Record<string, unknown>) {
  logRuntimeError(reason, details);
}

export function installRuntimeRecovery() {
  if (typeof window === "undefined") return () => undefined;

  const buildId = getBuildFingerprint();

  const onWindowError = (event: Event) => {
    const errorEvent = event as ErrorEvent;
    const target = errorEvent.target;

    if (target instanceof HTMLScriptElement || target instanceof HTMLLinkElement) {
      const resourceUrl =
        target instanceof HTMLScriptElement
          ? target.src
          : target instanceof HTMLLinkElement
            ? target.href
            : "unknown";

      console.error("[RuntimeRecovery] Asset 404/fail:", resourceUrl);
      logRuntimeError("asset-load-error", { resourceUrl });
      return;
    }

    const message = extractMessage(errorEvent.error ?? errorEvent.message);

    if (isChunkLoadFailure(message)) {
      logRuntimeError("chunk-load-error", { message });
    }
  };

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    const message = extractMessage(event.reason);
    if (isChunkLoadFailure(message)) {
      logRuntimeError("unhandled-rejection", { message });
    }
  };

  // Log build fingerprint for version tracking
  console.info(`[RuntimeRecovery] Guard ativo (log-only) — build: ${buildId}`);

  window.addEventListener("error", onWindowError, true);
  window.addEventListener("unhandledrejection", onUnhandledRejection);

  return () => {
    window.removeEventListener("error", onWindowError, true);
    window.removeEventListener("unhandledrejection", onUnhandledRejection);
  };
}
